import { Step } from "../../types/Step";
import NavigatorItemId from "../../App/types/NavigatorItemId";
import AllMedicalQuestionsEvaluator from "../../steps/Medical/compositions/AllMedicalQuestionsEvaluator";
import { DiabeticFormEvaluator } from "../../steps/Medical/compositions/ComposedDiabeticMedicalQuestions";
import { GeneralMedicalQuestionsEvaluator } from "../../steps/Medical/compositions/ComposedGeneralMedicalQuestions";
import { PacemakerFormEvaluator } from "../../steps/Medical/compositions/ComposedPacemakerMedicalQuestions";
import { RadiationFormEvaluator } from "../../steps/Medical/compositions/ComposedRadiationMedicalQuestions";
import { FlowStepsConfiguration } from "../types/IFlow";

/** The configured steps for screening qualified patients including qualify and medical steps. */
export const screeningStepConfigurations: FlowStepsConfiguration = {
  [Step.qualify]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.qualify,
    nextStep: ({ patient: { height, weight, dateOfBirth }, disqualifyingReasonsCState }) =>
      height && weight && dateOfBirth && disqualifyingReasonsCState ? Step.medicalGeneral : undefined
  },
  [Step.medicalGeneral]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.medicalGeneral,
    nextStep: ({ medicalForm, disqualifyingReasonsCState }) => {
      if (disqualifyingReasonsCState && GeneralMedicalQuestionsEvaluator.isComplete(medicalForm))
        return Step.medicalPacemaker;
      return undefined;
    }
  },
  [Step.medicalPacemaker]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.medicalPacemaker,
    nextStep: ({ medicalForm, disqualifyingReasonsCState }) => {
      if (disqualifyingReasonsCState && PacemakerFormEvaluator.isComplete(medicalForm)) return Step.medicalRadiation;
      return undefined;
    }
  },
  [Step.medicalRadiation]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.medicalRadiation,
    nextStep: ({ medicalForm, disqualifyingReasonsCState }) => {
      if (disqualifyingReasonsCState && RadiationFormEvaluator.isComplete(medicalForm)) return Step.medicalDiabetic;
      return undefined;
    }
  },
  [Step.medicalDiabetic]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.medicalDiabetic,
    nextStep: ({ medicalForm, disqualifyingReasonsCState }) => {
      if (disqualifyingReasonsCState && DiabeticFormEvaluator.isComplete(medicalForm)) return Step.scheduling;
      return undefined;
    }
  }
};

/** The configured screening steps for experimental flows using a single combined medical step. */
export const experimentalScreeningStepConfigurations: FlowStepsConfiguration = {
  [Step.qualify]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.qualify,
    nextStep: ({ patient: { height, weight, dateOfBirth }, disqualifyingReasonsCState }) =>
      height && weight && dateOfBirth && disqualifyingReasonsCState ? Step.medicalAll : undefined
  },
  [Step.medicalAll]: {
    activeNavigatorItem: NavigatorItemId.Screening,
    key: Step.medicalAll,
    nextStep: ({ medicalForm, disqualifyingReasonsCState }) => {
      if (disqualifyingReasonsCState && AllMedicalQuestionsEvaluator.isComplete(medicalForm)) return Step.scheduling;
      return undefined;
    }
  }
};

/** The configured scheduling steps that support changing the center, with no confirmation step (books directly). */
export const noConfirmationCenterSelectableBookingStepConfigurations: FlowStepsConfiguration = {
  [Step.scheduling]: {
    activeNavigatorItem: NavigatorItemId.Scheduling,
    key: Step.scheduling,
    nextStep: null
  },
  [Step.centerSelection]: {
    activeNavigatorItem: NavigatorItemId.Scheduling,
    key: Step.centerSelection,
    nextStep: () => Step.scheduling
  }
};

/** The configured scheduling steps that do not allow for the center to be changed, with no confirmation step (books directly). */
export const noConfirmationStaticCenterBookingStepConfigurations: FlowStepsConfiguration = {
  [Step.scheduling]: noConfirmationCenterSelectableBookingStepConfigurations[Step.scheduling]
};
